import { getCategoryInfo, getModuleCountFromEntity } from '../../services/portfolioComponentService';
import type { SiteDataWithMetadata } from '../../redux/slices/portfolioSiteSelectionSlice';
import type { PortfolioComponentData } from '../../services/portfolioComponentService';

// Distinct colors for different models (colorblind-friendly palette)
export const MODEL_COLOR_PALETTE = [
  '#3B82F6', // Blue
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F43F5E', // Rose
];

export function getModelColor(_model: string, index: number): string {
  return MODEL_COLOR_PALETTE[index % MODEL_COLOR_PALETTE.length];
}

export interface ComponentFilter {
  category: string | null;
  manufacturer: string | null;
  model: string | null;
}

export interface ModelWithColor {
  model: string;
  total_count: number;
  site_count: number;
  siteIds: Set<string>;
  originalModels: string[];
  color: string;
}

/** Count entities matching the current filter for a given site */
export function getFilteredComponentCount(
  siteId: string,
  sitesData: SiteDataWithMetadata[],
  filter: ComponentFilter
): number {
  const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
  if (!siteData) return 0;

  if (!filter.category && !filter.manufacturer && !filter.model) {
    return siteData.entities.length;
  }

  // Special handling for modules: count modules from string entities
  if (filter.category === 'module') {
    let moduleCount = 0;
    for (const entity of siteData.entities) {
      moduleCount += getModuleCountFromEntity(entity);
    }
    return moduleCount;
  }

  return siteData.entities.filter(entity => {
    const componentType = entity.concept_id || entity.entity_type_id || (entity as any).pvcollada_component_type || '';
    const { category } = getCategoryInfo(componentType);
    if (filter.category && category !== filter.category) return false;
    if (filter.manufacturer) {
      const manufacturer = entity.manufacturer || (entity.properties as any)?.manufacturer || '';
      if (!manufacturer.toLowerCase().includes(filter.manufacturer.toLowerCase())) return false;
    }
    return true;
  }).length;
}

/** Get inverter and module model names for a site */
export function getSiteComponentInfo(
  siteId: string,
  sitesData: SiteDataWithMetadata[]
): { inverterModels: string[]; moduleModels: string[] } {
  const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
  if (!siteData) return { inverterModels: [], moduleModels: [] };

  const inverterModels = new Set<string>();
  const moduleModels = new Set<string>();

  for (const entity of siteData.entities) {
    const componentType = entity.concept_id || entity.entity_type_id || (entity as any).pvcollada_component_type || '';
    const { category } = getCategoryInfo(componentType);
    const model = entity.model || (entity.properties as any)?.model || entity.name || '';

    if (category === 'inverter' && model) {
      inverterModels.add(model);
    } else if (category === 'module' && model) {
      moduleModels.add(model);
    }
  }

  return {
    inverterModels: Array.from(inverterModels),
    moduleModels: Array.from(moduleModels),
  };
}

/** Check if a site has the selected model based on filteredModels */
export function siteHasSelectedModel(
  siteId: string,
  filter: ComponentFilter,
  filteredModels: ModelWithColor[]
): boolean {
  if (!filter.model) return true;
  const selectedModel = filteredModels.find(m => m.model === filter.model);
  if (selectedModel) {
    return selectedModel.siteIds.has(siteId);
  }
  return true;
}

/** Get marker color based on filter — category color or default green */
export function getMarkerColor(
  filter: ComponentFilter,
  portfolioData: PortfolioComponentData | null
): string {
  if (filter.category) {
    const summary = portfolioData?.summaries.find(s => s.component_category === filter.category);
    return summary?.color || '#10B981';
  }
  return '#10B981';
}

/** Get marker opacity based on filter selection */
export function getMarkerOpacity(
  siteId: string,
  isHovered: boolean,
  isSelected: boolean,
  filter: ComponentFilter,
  filteredModels: ModelWithColor[]
): number {
  if (isHovered || isSelected) return 0.95;
  if (filter.model) {
    const selectedModel = filteredModels.find(m => m.model === filter.model);
    if (selectedModel && !selectedModel.siteIds.has(siteId)) {
      return 0.25;
    }
  }
  return 0.7;
}

/** Calculate marker radius using log scale */
export function getMarkerRadius(count: number): number {
  const baseSize = 8;
  const maxSize = 24;
  return Math.min(baseSize + Math.log10(count + 1) * 6, maxSize);
}
